import useAlternativeUsdPairs from "@shared/features/bots/BotForm/General/useAlternativeUsdPairs";
import { ToggleField } from "@shared/features/bots/BotForm/common/types";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle";
import React, { CSSProperties } from "react";
import styled, { useTheme } from "styled-components";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles";
import { ALTERNATIVE_USD_PAIRS_TITLE } from "@shared/features/bots/BotForm/common/constants.ts";
import { openMessage } from "utils/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface AlternativeUsdPairsProps {
  style?: CSSProperties;
}

const AlternativeUsdPairs = ({ style }: AlternativeUsdPairsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const {
    options,
    errorMessage,
    warningMessage,
    shouldDisplay,
    resetShowEditWarning,
    onPersonalClick,
  } = useAlternativeUsdPairs({ openMessage });

  const getContent = () => {
    return (
      <Wrapper>
        {options.map((option) => {
          return (
            <ButtonWrapper
              key={option.name}
              onClick={option.onClick}
              $selected={option.selected}
            >
              <TextWithIcon
                coinOrExchangeName={option.name}
                iconType={ICON_TYPES.COIN}
                text={option.name}
              />
            </ButtonWrapper>
          );
        })}
      </Wrapper>
    );
  };

  if (!shouldDisplay) return null;

  return (
    <>
      <TitleWithToggle
        style={style}
        title={
          <FormFieldTitle style={{ margin: 0 }}>
            {t(ALTERNATIVE_USD_PAIRS_TITLE)}
          </FormFieldTitle>
        }
        content={
          <div>
            {getContent()}
            {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
          </div>
        }
        fieldType={ToggleField.ALTERNATIVE_USD_PAIRS}
        onOverrideChange={resetShowEditWarning}
        onPersonalClick={onPersonalClick}
      />
      {warningMessage && (
        <ErrorText style={{ color: theme.colors.gold }}>
          {warningMessage}
        </ErrorText>
      )}
    </>
  );
};

export default AlternativeUsdPairs;

const Wrapper = styled.div`
  display: flex;
  column-gap: 8px;
`;
const ButtonWrapper = styled.div<{ $selected?: boolean }>`
  border-radius: 6px;
  padding: 4px 10px;
  background-color: ${(p) =>
    p.$selected ? p.theme.colors.primaryBlue : p.theme.colors.lightBlue};
  color: ${(p) =>
    p.$selected ? p.theme.colors.buttonText : p.theme.colors.primaryBlue};
  cursor: pointer;
  transition: all 0.25s;
  display: flex;
  align-items: center;
  user-select: none;
`;
